import { Injectable } from '@angular/core';
import { EscalaService } from './escala.service';
import {
  BehaviorSubject,
  Observable,
  catchError,
  finalize,
  map,
  of,
  tap,
  throwError,
} from 'rxjs';
import { Escala, EscalaPeriodo } from 'src/app/models/escala';
import {
  Estatistica,
  EstatisticaColaborador,
  FiltroEstatistica,
} from 'src/app/models/estatistica';
import { HttpClient } from '@angular/common/http';
import { ScaleStore } from '../scale.store';
import { EndPoint, getEndpoint } from 'src/app/models/endpoint';
import { LoadingService } from '../loading.service';
import { AuthStore } from '../auth.store';
import { ItensEscalaPeriodoColaborador } from 'src/app/models/itens_colaboradores';
import { SelectedItem } from 'src/app/models/table-itens';

@Injectable()
export class EscalaHttpService extends EscalaService {
  constructor(
    private http: HttpClient,
    scaleStore: ScaleStore,
    private loadingService: LoadingService,
    private authStore: AuthStore
  ) {
    super(scaleStore);
  }

  colaboradoresMock: EstatisticaColaborador[] = [
    {
      nome: 'Ana Silva',
      solicitacoes: 5,
      trocas: 2,
      afastamentos: 1,
      ferias: 3,
    },
    {
      nome: 'Bruno Costa',
      solicitacoes: 3,
      trocas: 1,
      afastamentos: 0,
      ferias: 2,
    },
    {
      nome: 'Carla Souza',
      solicitacoes: 7,
      trocas: 3,
      afastamentos: 2,
      ferias: 4,
    },
    {
      nome: 'Diego Lima',
      solicitacoes: 4,
      trocas: 0,
      afastamentos: 1,
      ferias: 1,
    },
    {
      nome: 'Elisa Mendes',
      solicitacoes: 6,
      trocas: 2,
      afastamentos: 0,
      ferias: 3,
    },
    {
      nome: 'Felipe Rocha',
      solicitacoes: 2,
      trocas: 1,
      afastamentos: 1,
      ferias: 0,
    },
    {
      nome: 'Gabriela Dias',
      solicitacoes: 8,
      trocas: 4,
      afastamentos: 3,
      ferias: 5,
    },
    {
      nome: 'Hugo Almeida',
      solicitacoes: 3,
      trocas: 0,
      afastamentos: 0,
      ferias: 2,
    },
    {
      nome: 'Isabela Ferreira',
      solicitacoes: 5,
      trocas: 2,
      afastamentos: 1,
      ferias: 3,
    },
    {
      nome: 'João Pereira',
      solicitacoes: 4,
      trocas: 1,
      afastamentos: 0,
      ferias: 2,
    },
    {
      nome: 'Kelly Santos',
      solicitacoes: 6,
      trocas: 3,
      afastamentos: 2,
      ferias: 4,
    },
    {
      nome: 'Lucas Oliveira',
      solicitacoes: 3,
      trocas: 0,
      afastamentos: 1,
      ferias: 1,
    },
    {
      nome: 'Marina Lopes',
      solicitacoes: 7,
      trocas: 2,
      afastamentos: 0,
      ferias: 3,
    },
    {
      nome: 'Nelson Carvalho',
      solicitacoes: 5,
      trocas: 1,
      afastamentos: 1,
      ferias: 2,
    },
    {
      nome: 'Olga Martins',
      solicitacoes: 4,
      trocas: 0,
      afastamentos: 0,
      ferias: 1,
    },
    {
      nome: 'Pedro Henrique',
      solicitacoes: 6,
      trocas: 3,
      afastamentos: 2,
      ferias: 4,
    },
    {
      nome: 'Quiteria Gomes',
      solicitacoes: 3,
      trocas: 1,
      afastamentos: 0,
      ferias: 2,
    },
    {
      nome: 'Rafael Barbosa',
      solicitacoes: 5,
      trocas: 2,
      afastamentos: 1,
      ferias: 3,
    },
    {
      nome: 'Sofia Ribeiro',
      solicitacoes: 7,
      trocas: 4,
      afastamentos: 3,
      ferias: 5,
    },
    {
      nome: 'Tiago Moreira',
      solicitacoes: 4,
      trocas: 0,
      afastamentos: 0,
      ferias: 2,
    },
  ];

  private estatisticas: BehaviorSubject<Estatistica[]> = new BehaviorSubject<
    Estatistica[]
  >([]);
  public estatisticas$: Observable<Estatistica[]> =
    this.estatisticas.asObservable();

  public carregaEscalas(localId: number): Observable<Escala[]> {
    return this.http
      .get<Escala[]>(`${getEndpoint(EndPoint.ESCALA)}?localId=${localId}`)
      .pipe(
        map((response) => {
          if (response) {
            return response.map((escala) => {
              escala.quantidadeSolicitacoesPendentes = 0;
              return escala;
            });
          }
          return [];
        }),
        catchError((err: any) => {
          const message = 'Erro ao carregar as escalas.';
          this.loadingService.presentToast(message);
          return throwError(err);
        }),
        tap((response) => {
          if (response[0]) {
            this.scaleStore.setEscalas(response[0].local!, response);
          }
        })
      );
  }

  public getEscalaPeriodo(
    id: number,
    periodo: string = '2025-04'
  ): Observable<EscalaPeriodo> {
    this.loadingService.presentLoading();
    return this.http
      .get<EscalaPeriodo>(
        `${getEndpoint(
          EndPoint.ESCALA_PERIODO
        )}?escalaId=${id}&periodo=${periodo}`
      )
      .pipe(
        map((response) => {
          if (response) {
            return response;
          }
          return {} as EscalaPeriodo;
        }),
        catchError((err: any) => {
          return throwError(err);
        }),
        tap((res) => {
          this.scaleStore.updateEscala(res);
        }),
        finalize(async () => {
          await this.loadingService.dismissLoading();
        })
      );
  }

  public override gerarPdf(
    escalaId: number,
    periodo: string
  ): Observable<Blob> {
    return this.http.get(
      `${getEndpoint(
        EndPoint.ESCALA_PERIODO
      )}/pdf?escalaId=${escalaId}&periodo=${periodo}`,
      {
        responseType: 'blob',
      }
    );
  }

  public override createPeriodo(
    id: number,
    periodo: string
  ): Observable<EscalaPeriodo> {
    this.loadingService.presentLoading();
    return this.http
      .post<EscalaPeriodo>(`${getEndpoint(EndPoint.ESCALA_PERIODO)}`, {
        escalaId: id,
        periodo: periodo,
      })
      .pipe(
        map((response) => {
          if (response) {
            return response;
          }
          return {} as EscalaPeriodo;
        }),
        catchError((err: any) => {
          this.loadingService.dismissLoading();
          const message = 'Erro ao carregar a escala';
          this.loadingService.presentToast(message);
          return throwError(err);
        }),
        tap((res) => {
          this.loadingService.dismissLoading();
          this.scaleStore.updateEscala(res);
        })
      );
  }

  public criaEscala(escala: Escala): Observable<Escala> {
    this.loadingService.presentLoading();
    return this.http
      .post<Escala>(getEndpoint(EndPoint.ESCALA), {
        ...escala,
      })
      .pipe(
        map((response) => {
          if (response) {
            return response;
          }
          return {} as Escala;
        }),
        catchError((err: any) => {
          this.loadingService.dismissLoading();
          const message = 'Erro ao criar uma escala.';
          this.loadingService.presentToast(message);
          return throwError(err);
        }),
        tap((res) => {
          this.loadingService.dismissLoading();
          this.scaleStore.addEscala(res);
        })
      );
  }

  public filtrarEscalaPorLocal(localId: number): Observable<Escala[]> {
    return this.http
      .get<Escala[]>(`${getEndpoint(EndPoint.ESCALA)}?localId=${localId}`)
      .pipe(
        map((response) => {
          if (response) {
            return response;
          }
          return [];
        })
      );
  }

  public deletarEscala(escalaId: number): Observable<any> {
    return this.http.delete(`${getEndpoint(EndPoint.ESCALA)}/${escalaId}`).pipe(
      catchError((err: any) => {
        const message = 'Erro ao excluir um local.';
        this.loadingService.presentToast(message);
        return throwError(err);
      })
    );
  }

  public atualizarEscala(
    escalaId: number,
    changes: Partial<Escala>
  ): Observable<Escala> {
    this.loadingService.presentLoading();
    return this.http
      .put<Escala>(`${getEndpoint(EndPoint.ESCALA)}/${escalaId}`, {
        ...changes,
        id: escalaId,
      })
      .pipe(
        map((response) => {
          if (response) {
            return response;
          }
          return {} as Escala;
        }),
        catchError((err: any) => {
          this.loadingService.dismissLoading();
          const message = 'Erro ao atualizar uma escala.';
          this.loadingService.presentToast(message);
          return throwError(err);
        }),
        tap((r: any) => {
          this.scaleStore.updateEscala({
            ...changes,
            id: escalaId,
          });
          this.loadingService.dismissLoading();
        })
      );
  }

  public carregaEstatisticas(): Observable<Estatistica[]> {
    // console.log(this.estatisticas$)
    return this.estatisticas$;
  }

  public criaEstatistica(estatistica: Estatistica): Observable<Estatistica> {
    let estatisticas = this.estatisticas.getValue();
    if (estatisticas.length === 0) {
      estatistica.id = 1;
    } else {
      estatistica.id = estatisticas[estatisticas.length - 1].id + 1;

      //desativa dps
      // estatistica.idEscala = estatisticas[estatisticas.length -1].id + 1;
    }
    console.log(estatistica);
    estatisticas.push(estatistica);
    this.estatisticas.next(estatisticas);
    console.log(this.estatisticas);
    return of(estatistica);
  }

  public filtrarEstatistica(estatisticaId: number): Observable<Estatistica> {
    const estatisticas = this.estatisticas.getValue();
    const estatistica = estatisticas.find(
      (estatistica) => estatistica.id === estatisticaId
    )!;

    return of(estatistica);
  }

  public override getEscalas(localId: number): Observable<Escala[]> {
    if (this.authStore.isAdmin()) {
      return this.scaleStore.escalas$.pipe(
        map((res) => {
          if (res.find((escala) => escala.local.id === localId)) {
            return res.find((escala) => escala.local.id === localId)!.escalas;
          }
          return [];
        })
      );
    } else {
      return this.scaleStore.escalas$.pipe(
        map((res) => {
          if (res.find((escala) => escala.local.id === localId)) {
            return res
              .find((escala) => escala.local.id === localId)!
              .escalas.filter((e) => {
                console.log(this.authStore.getUserGestor());
                return e.gestor === this.authStore.getUserGestor().id;
              });
          }
          return [];
        })
      );
    }
  }

  public filtrarEstatisticaPorEscala(
    escalaId: number,
    filtro: FiltroEstatistica
  ): Observable<Estatistica> {
    return this.http.get<Estatistica>(
      `${getEndpoint(
        EndPoint.ESCALA
      )}/${escalaId}/estatisticas?${filtro.getFiltro()}`
    );
    // Calcula os totais
    // const totalSolicitacoes = this.colaboradoresMock.reduce((sum, col) => sum + col.solicitacoes, 0);
    // const totalAfastamentos = this.colaboradoresMock.reduce((sum, col) => sum + col.afastamentos, 0);
    // const totalFerias = this.colaboradoresMock.reduce((sum, col) => sum + col.ferias, 0);
    // const totalTrocas = this.colaboradoresMock.reduce((sum, col) => sum + col.trocas, 0);

    // return of({
    //   id: 1,
    //   quantidadeSolicitacoes: totalSolicitacoes, // 95
    //   quantidadeAfastamento: totalAfastamentos, // 19
    //   quantidadeFerias: totalFerias, // 52
    //   quantidadeAniversarios: 5, // Valor fictício
    //   quantidadeTroca: totalTrocas, // 32
    //   colaboradores: this.colaboradoresMock,
    // } as Estatistica)
  }

  public override removerColaboradorEscala(
    escalaId: number,
    colaboradorId: number
  ): Observable<any> {
    this.loadingService.presentLoading();

    return this.http
      .delete(
        `${getEndpoint(
          EndPoint.ESCALA
        )}/delete/colaborador?idEscala=${escalaId}&idColaborador=${colaboradorId}`
      )
      .pipe(
        tap((_) => {
          this.loadingService.dismissLoading();
        })
      );
  }

  public aplicarEmMassaEscala(
    turnoId: number,
    items: SelectedItem[]
  ): Observable<any> {
    this.loadingService.presentLoading();
    console.log(items);

    const newList = [];

    for (let item of items) {
      let newItem: any = {
        turnoId,
        escalaPeriodoColaboradorId: item.escalaPeriodoColaboradorId,
        ponto: item.item?.ponto ?? false,
        setorId: item.setorId,
        dataEscala: new Date(item.date).toJSON(),
      };
      if (item.item?.id) newItem.id = item.item.id;
      newList.push(newItem);
    }

    console.log(newList);

    return this.http
      .post(`${getEndpoint(EndPoint.ITEM_COLABORADOR)}/lista`, newList)
      .pipe(
        catchError((err: any) => {
          if (err.error.message) {
            this.loadingService.presentToast(err.error.message, 'danger');
          } else {
            const message = 'Erro ao aplicar em massa';
            this.loadingService.presentToast(message);
          }

          return throwError(err);
        }),
        tap((r) => {
          console.log(r);
          this.loadingService.dismissLoading();
        })
      );
  }

  public override carregarItensColaboradores(
    ids: number[]
  ): Observable<ItensEscalaPeriodoColaborador[]> {
    return this.http
      .post<ItensEscalaPeriodoColaborador[]>(
        `${getEndpoint(EndPoint.ITEM_COLABORADOR)}/consulta-por-ids`,
        ids
      )
      .pipe(
        catchError((err: any) => {
          if (err.error.message) {
            this.loadingService.presentToast(err.error.message, 'danger');
          } else {
            const message = 'Erro ao aplicar em massa';
            this.loadingService.presentToast(message);
          }

          return throwError(err);
        }),
        tap((r) => {
          console.log(r);
          this.loadingService.dismissLoading();
        })
      );
  }

  public override removerColaboradorPeriodo(
    escalaPeriodoColaboradorId: number
  ): Observable<any> {
    this.loadingService.presentLoading();
    return this.http
      .delete(
        `${getEndpoint(
          EndPoint.ESCALA_PERIODO
        )}/colaboradores/${escalaPeriodoColaboradorId}`
      )
      .pipe(
        tap((_) => {
          this.loadingService.dismissLoading();
        })
      );
  }

  public override rolarEscala(
    escalaId: number,
    periodo: string
  ): Observable<any> {
    this.loadingService.presentLoading();
    return this.http
      .post<any>(`${getEndpoint(EndPoint.ESCALA_PERIODO)}/rolar-escala`, {
        escalaId,
        periodo,
      })
      .pipe(
        catchError((err: any) => {
          console.log(err);
          if (err.error.error.message) {
            this.loadingService.presentToast(err.error.error.message, 'danger');
          } else {
            const message = 'Erro ao rolar escala';
            this.loadingService.presentToast(message);
          }
          this.loadingService.dismissLoading();
          return throwError(err);
        }),
        tap((r) => {
          console.log(r);
          this.loadingService.dismissLoading();
        })
      );
  }

  public override duplicarEscala(escalaId: number): Observable<any> {
    this.loadingService.presentLoading();
    return this.http
      .post<any>(`${getEndpoint(EndPoint.ESCALA)}/${escalaId}/duplicar`, {})
      .pipe(
        catchError((err: any) => {
          if (err.error.message) {
            this.loadingService.presentToast(err.error.message, 'danger');
          } else {
            const message = 'Erro ao duplicar escala';
            this.loadingService.presentToast(message);
          }
          return throwError(err);
        }),
        tap((r) => {
          console.log(r);
        }),
        finalize(() => {
          this.loadingService.dismissLoading();
        })
      );
  }

  public override carregarEscalasAlocadoColaborador(
    idColaborador: number
  ): Observable<Escala[]> {
    this.loadingService.presentLoading();
    return this.http
      .get<Escala[]>(
        `${getEndpoint(EndPoint.ESCALA)}/colaborador/${idColaborador}`
      )
      .pipe(
        finalize(() => {
          this.loadingService.dismissLoading();
        })
      );
  }

  public override atualizarDetalhesEscala(
    escalaPeriodoId: number,
    publicado: boolean,
    ocultado: boolean,
    observacao: string
  ): Observable<any> {
    this.loadingService.presentLoading();
    return this.http
      .put<any>(`${getEndpoint(EndPoint.ESCALA_PERIODO)}/${escalaPeriodoId}`, {
        publicado,
        ocultado,
        observacao,
      })
      .pipe(
        catchError((err: any) => {
          if (err.error.message) {
            this.loadingService.presentToast(err.error.message, 'danger');
          } else {
            const message = 'Erro ao atualizar detalhes da escala';
            this.loadingService.presentToast(message);
          }
          this.loadingService.dismissLoading();
          return throwError(err);
        }),
        tap((r) => {
          console.log(r);
          this.loadingService.dismissLoading();
        })
      );
  }
}
